import React, { useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Group,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconEye, IconEyeOff, IconSearch } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { listOperationsFromSpec } from "../utils/openapi-ops.ts";
import { useUpdateTabMutation } from "../queries/tab-query.ts";
import type { ITab } from "../types/tab.types.ts";
import classes from "./openapi-operations-tree.module.css";

type Props = {
  tab: ITab;
  spaceSlug: string;
  customDomain?: string | null;
  canManage: boolean;
};

function publicHost(spaceSlug: string, customDomain?: string | null): string {
  return customDomain && customDomain.trim().length > 0
    ? customDomain.trim()
    : `${spaceSlug}.commitdoc.com`;
}

function methodColor(method: string): string {
  switch (method) {
    case "get":
      return "teal";
    case "post":
      return "blue";
    case "put":
      return "orange";
    case "patch":
      return "violet";
    case "delete":
      return "red";
    default:
      return "gray";
  }
}

export function OpenApiOperationsTree({
  tab,
  spaceSlug,
  customDomain,
  canManage,
}: Props) {
  const { t } = useTranslation();
  const update = useUpdateTabMutation(tab.spaceId);
  const host = publicHost(spaceSlug, customDomain);

  const [filter, setFilter] = useState("");

  // Admin sidebar shows the DRAFT — that's the spec the editor is
  // currently working on. Falls back to published for tabs that have
  // been published but never had a draft (post-publish state).
  const operations = useMemo(
    () =>
      listOperationsFromSpec(
        tab.openapiDraftSpec ?? tab.openapiPublishedSpec ?? null,
      ),
    [tab.openapiDraftSpec, tab.openapiPublishedSpec],
  );

  const hidden = useMemo(
    () => new Set(tab.openapiHiddenOps ?? []),
    [tab.openapiHiddenOps],
  );

  // Filter against title / path / method / tag. Case-insensitive
  // substring match — fancy enough for the sidebar without the cost of
  // fuzzy-matching libs.
  const filteredOps = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return operations;
    return operations.filter(
      (op) =>
        op.title.toLowerCase().includes(q) ||
        op.path.toLowerCase().includes(q) ||
        op.method.toLowerCase().includes(q) ||
        (op.tag ?? "").toLowerCase().includes(q),
    );
  }, [operations, filter]);

  const groups = useMemo(() => {
    const map = new Map<string, typeof operations>();
    for (const op of filteredOps) {
      const k = op.tag ?? "—";
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(op);
    }
    return Array.from(map.entries());
  }, [filteredOps]);

  const toggle = (e: React.MouseEvent, slug: string) => {
    e.preventDefault();
    e.stopPropagation();
    const next = new Set(hidden);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    update.mutate(
      { tabId: tab.id, openapiHiddenOps: Array.from(next) },
      {
        onError: (err: any) => {
          notifications.show({
            message:
              err?.response?.data?.message ?? t("Failed to update visibility"),
            color: "red",
          });
        },
      },
    );
  };

  if (operations.length === 0) {
    return (
      <Stack gap="xs" px="md" py="sm">
        <Text size="sm" c="dimmed">
          {t("This OpenAPI tab has no operations. Edit the tab to upload a spec.")}
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap={4} h="100%">
      <TextInput
        size="xs"
        placeholder={t("Filter operations…")}
        value={filter}
        onChange={(e) => setFilter(e.currentTarget.value)}
        leftSection={<IconSearch size={12} />}
        className={classes.filter}
      />
      {filteredOps.length === 0 ? (
        <Text size="xs" c="dimmed" px="md" py="xs">
          {t("No operations match the filter.")}
        </Text>
      ) : null}
      <ScrollArea type="hover" className={classes.scroll}>
      <Stack gap={2} className={classes.list}>
        {groups.map(([groupName, ops]) => (
          <div key={groupName} className={classes.group}>
            <Text size="xs" fw={600} c="dimmed" className={classes.groupTitle}>
              {groupName}
            </Text>
            {ops.map((op) => {
              const isHidden = hidden.has(op.slug);
              const publicUrl = `https://${host}/openapi/${tab.id}/${op.slug}`;
              return (
                <a
                  key={op.slug}
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.row}
                  data-hidden={isHidden ? "true" : undefined}
                >
                <Group gap={6} wrap="nowrap" style={{ width: "100%" }}>
                  <Badge
                    size="xs"
                    variant="light"
                    color={methodColor(op.method)}
                    radius="sm"
                    className={classes.method}
                  >
                    {op.method.toUpperCase()}
                  </Badge>
                  <Text
                    size="xs"
                    truncate
                    className={classes.title}
                    title={`${op.method.toUpperCase()} ${op.path}`}
                  >
                    {op.title}
                  </Text>
                  {canManage && (
                    <Tooltip
                      label={
                        isHidden
                          ? t("Show on public site")
                          : t("Hide from public site")
                      }
                      withArrow
                      openDelay={300}
                    >
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        color="gray"
                        onClick={(e) => toggle(e, op.slug)}
                        loading={update.isPending}
                        className={classes.eye}
                      >
                        {isHidden ? (
                          <IconEyeOff size={14} />
                        ) : (
                          <IconEye size={14} />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
                </a>
              );
            })}
          </div>
        ))}
      </Stack>
    </ScrollArea>
    </Stack>
  );
}
